// 5 sources of candidate data
// 1) User store (preview mode - authed user)
// 2) Manual candidate data (dialog entry - `?manual_candidate=true`)
// 3) Query params (`?organization=...&identifier=...&firstName=...&lastName=...`)
// 4) Candidate ID query param (`?candidateId=...`)
// 5) Iframe or post message
// 6) Demo iframe (uses static data - `?demo=true`)

import * as Sentry from '@sentry/vue'
import { useLocalStorage } from '@vueuse/core'
import { useRouteQuery } from '@vueuse/router'
import { defineStore } from 'pinia'
import { useUserStore } from '@/stores/user'
import type { Candidate } from '~/types/Candidate'
import type { Unsaved } from '~/types/Unsaved'
import { CandidateService } from '~/services/api/candidate/candidate'
import { useCandidateSseStore } from '~/stores/candidate/candidate-sse'

export const useCandidateStore = defineStore('candidate', () => {
  // OTHER SERVICES =================================================================================
  const { showDialog } = useDialogService()
  const candidateService = new CandidateService()
  nextTick(() => useCandidateSseStore()) // next time solves the init race condition

  // STORES =========================================================================================
  const userStore = useUserStore()

  // GLOBAL STATE ===================================================================================
  const candidate = ref<Candidate | null>(null)

  // ROUTE QUERY PARAMS =============================================================================
  const queryIdentifier = useRouteQuery('identifier')
  const queryOrganization = useRouteQuery('organization')
  const queryFirstName = useRouteQuery('firstName')
  const queryLastName = useRouteQuery('lastName')
  const queryDemo = useRouteQuery('demo')
  const queryManualCandidate = useRouteQuery('manual_candidate')
  const queryCandidateId = useRouteQuery('candidateId')
  const unsafeLocalStorageCandidateData = useLocalStorage<
    Candidate | Unsaved<Candidate> | null
  >('candidateData', null, {
    serializer: {
      read: (value) => JSON.parse(value),
      write: (value) => JSON.stringify(value),
    },
  })
  const localStorageCandidateData = computed<
    Candidate | Unsaved<Candidate> | null
  >(() => {
    if (!unsafeLocalStorageCandidateData.value) return null

    if (
      typeof unsafeLocalStorageCandidateData.value.firstName === 'string' &&
      typeof unsafeLocalStorageCandidateData.value.lastName === 'string' &&
      typeof unsafeLocalStorageCandidateData.value.organization === 'string' &&
      typeof unsafeLocalStorageCandidateData.value.identifier === 'string'
    ) {
      if (
        'id' in unsafeLocalStorageCandidateData.value &&
        'createdAt' in unsafeLocalStorageCandidateData.value &&
        'updatedAt' in unsafeLocalStorageCandidateData.value &&
        typeof unsafeLocalStorageCandidateData.value.id === 'string' &&
        typeof unsafeLocalStorageCandidateData.value.createdAt === 'string' &&
        typeof unsafeLocalStorageCandidateData.value.updatedAt === 'string'
      ) {
        return unsafeLocalStorageCandidateData.value as Candidate
      }

      return unsafeLocalStorageCandidateData.value as Unsaved<Candidate>
    }

    return null
  })

  async function setCandidateData(candidateData: Unsaved<Candidate>) {
    const { data, error } = await candidateService.upsertCandidate({
      ...candidateData,
      organization:
        queryOrganization.value && typeof queryOrganization.value === 'string'
          ? queryOrganization.value
          : 'Traverse Studio',
    })

    if (error !== null) {
      console.error('Error getting full candidate data:', error)
      return null
    }

    candidate.value = data

    unsafeLocalStorageCandidateData.value = data

    return data
  }

  async function loadCandidateDataById(candidateId: string) {
    const { data, error } = await candidateService.getCandidateById(candidateId)

    if (error !== null) {
      console.error('Error getting full candidate data:', error)
      return null
    }

    candidate.value = data

    return data
  }

  // 5) IFRAME DEMO =================================================================================
  const useIframeDemo = computed(
    () => queryDemo.value !== null && queryDemo.value === 'true',
  )

  // 3) QUERY PARAMS & 5) IFRAME DEMO ===============================================================
  const candidateInQueryParams = computed<Unsaved<Candidate> | null>(() => {
    // !TEMP FOR DEMO
    if (useIframeDemo.value) {
      return {
        identifier: 'iframe',
        organization: 'demo',
        firstName: 'Nic',
        lastName: 'Pullen',
      }
    }

    // Check for query keys
    if (
      !queryIdentifier.value ||
      !queryOrganization.value ||
      !queryFirstName.value ||
      !queryLastName.value ||
      typeof queryIdentifier.value !== 'string' ||
      typeof queryOrganization.value !== 'string' ||
      typeof queryFirstName.value !== 'string' ||
      typeof queryLastName.value !== 'string'
    ) {
      return null
    }

    return {
      identifier: queryIdentifier.value,
      organization: queryOrganization.value,
      firstName: queryFirstName.value,
      lastName: queryLastName.value,
    }
  })

  // 2) MANUAL CANDIDATE DATA =======================================================================
  const useManualCandidateData = computed(
    () => queryManualCandidate.value !== undefined,
  )

  async function getManualCandidateData() {
    const candidateData = await showDialog<Unsaved<Candidate>>({
      type: 'manual-candidate-data',
    })

    if (
      candidateData &&
      queryOrganization.value &&
      typeof queryOrganization.value === 'string'
    ) {
      candidateData.organization = queryOrganization.value
    }

    return candidateData
  }

  // 4) POST MESSAGE =================================================================================

  async function loadCandidateData() {
    // candidate.value = null

    // 0) ALREADY LOADED
    if (candidate.value !== null) {
      return candidate.value
    }

    // 1) USER STORE (PREVIEW MODE)
    await userStore.$ensureInitialised()

    if (userStore.userCandidateData !== null) {
      await setCandidateData(userStore.userCandidateData)
      return candidate.value as Candidate | null
    }

    // 2) MANUAL CANDIDATE DATA
    if (useManualCandidateData.value) {
      if (localStorageCandidateData.value !== null) {
        await setCandidateData(localStorageCandidateData.value)
        return candidate.value as Candidate | null
      }

      const candidateDataFromDialog = await getManualCandidateData()

      if (!candidateDataFromDialog) return null

      await setCandidateData(candidateDataFromDialog)
      return candidate.value as Candidate | null
    }

    // 3) QUERY PARAMS
    if (candidateInQueryParams.value !== null) {
      await setCandidateData(candidateInQueryParams.value)
      return candidate.value as Candidate | null
    }

    // 4) CANDIDATE ID QUERY PARAM
    if (queryCandidateId.value && typeof queryCandidateId.value === 'string') {
      await loadCandidateDataById(queryCandidateId.value)
      return candidate.value as Candidate | null
    }

    // 5) IFRAME OR POST MESSAGE
    // ! Temporarily removed - hopefully not needed
    // const { stop: stopPolling } = usePoll(refreshInteraction, 1000)

    // postIframeMessage(interaction.value.simulationId, interaction.value.id)

    // await waitUntil(() => isInteractionReady.value, {
    //   timeoutMs: 10000,
    // })

    // stopPolling()

    // if (!interaction.value.candidateId) {
    //   errorMessage.value = 'Could not load candidate data'
    //   hasError.value = true
    //   isLoading.value = false
    //   return
    // }

    // POPULATE LOCAL CANDIDATE DATA
    // const { data, error } = await InteractionService.getCandidateById(
    //   interaction.value.simulationId,
    //   interaction.value.id,
    //   interaction.value.candidateId,
    // )

    // if (error !== null) {
    //   errorMessage.value = 'Could not load candidate data'
    //   hasError.value = true
    //   isLoading.value = false
    //   return
    // }

    // candidate.value = data

    return null
  }

  // function postIframeMessage(simulationId: string, interactionId: string) {
  //   top?.postMessage({
  //     type: 'MIMICLY_CANDIDATE_DATA_REQUEST',
  //     simulationId,
  //     interactionId,
  //   })
  // }

  watch(
    () => candidate.value,
    (newCandidateData) => {
      if (userStore.user !== null) return

      Sentry.setUser({
        id: `candidate:${newCandidateData?.organization || '[no organization]'}::${newCandidateData?.identifier || '[no identifier]'}`,
      })
    },
  )

  function $reset() {
    candidate.value = null
  }

  return {
    candidate,
    loadCandidateData,
    setCandidateData,
    localStorageCandidateData,
    $reset,
    loadCandidateDataById,
  }
})
